import { axiosJSONInstance } from '@/config';
import {
  PickupSlotsResponse,
  PreferredTiming,
  ReslotSummary,
  SlotResponseResult,
  TimeSlotEditResult,
} from '@/types';

// Fetch the merchant's pickup time-slot options
export const fetchPickupSlots = async (): Promise<PickupSlotsResponse> => {
  const response = await axiosJSONInstance.get('/merchants/pick-up-slots');
  return response.data;
};

// Update the merchant's preferred pickup time slot.
//
// v2-task-9: PUT /merchants/pick-up-slots/preference is being extended
// (separate backend task) to accept an optional `auto_select_next_slot`
// alongside the required `time_slot_code`, echoing back the persisted
// `{ time_slot_code, auto_select_next_slot }` pair. `autoSelectNextSlot` is
// only included in the request body when explicitly provided (not merely
// `undefined`) so callers that don't touch the toggle never send it.
//
// v2-task-10fe (T-SW, already shipped backend-side): the SAME endpoint now
// also accepts an optional `recurring_pickup_enabled`, following the exact
// same "only send when explicitly provided" rule.
//
// v1.1 (preferred-timing within window): the SAME endpoint also accepts an
// optional `preferred_timing` bucket (null = Anytime, else early/mid/late) —
// the merchant's requested timing inside their preferred slot window. Sent ONLY
// when explicitly provided (undefined = leave untouched); `null` is a valid
// explicit "Anytime" write.
// Slot-change consolidation: the endpoint now re-slots the merchant's open
// same-day pickups onto the newly chosen slot and echoes back
// `reslotted: { moved, skipped }` (skipped = route-locked pickups left in
// place) so the caller can tell the merchant what actually moved.
export const updateSlotPreference = async (
  timeSlotCode: string,
  autoSelectNextSlot?: boolean,
  recurringPickupEnabled?: boolean,
  preferredTiming?: PreferredTiming | null,
): Promise<ReslotSummary> => {
  const response = await axiosJSONInstance.put('/merchants/pick-up-slots/preference', {
    time_slot_code: timeSlotCode,
    ...(autoSelectNextSlot !== undefined ? { auto_select_next_slot: autoSelectNextSlot } : {}),
    ...(recurringPickupEnabled !== undefined
      ? { recurring_pickup_enabled: recurringPickupEnabled }
      : {}),
    ...(preferredTiming !== undefined ? { preferred_timing: preferredTiming } : {}),
  });
  return response.data?.object?.reslotted ?? { moved: 0, skipped: 0, relocated: 0 };
};

// Update a single pickup's time slot. v1.1: optionally carries the chosen
// preferred timing bucket — sent only when provided (null = explicit Anytime).
//
// Routed-pickup relocation (backend fix): the enveloped `object` now carries
// `relocated_from_route` (true when this edit moved an already-routed pickup
// onto the new slot — Fleet re-plans the pickup agent). Previously this response
// was discarded (returned void); it is now surfaced so the caller can warn the
// merchant. Falls back to an empty object when the envelope omits `object`.
export const updatePickupTimeSlot = async (
  uid: string,
  timeSlotCode: string,
  preferredTiming?: PreferredTiming | null,
): Promise<TimeSlotEditResult> => {
  const response = await axiosJSONInstance.patch(`/merchants/pick-ups/${uid}/time-slot`, {
    time_slot_code: timeSlotCode,
    ...(preferredTiming !== undefined ? { preferred_timing: preferredTiming } : {}),
  });
  return response.data?.object ?? {};
};

// Respond to a pickup's slot-confirmation prompt (accept the suggested slot,
// change to a different one, or defer the decision). v1.1: a `change` response
// may carry the chosen preferred timing bucket alongside the new slot code.
//
// The enveloped response (`response.data.object`, NOT unwrapped by the axios
// interceptor) describes the slot the pickup ACTUALLY landed on — for a defer
// the backend rebooks it onto its ORIGINAL slot on the next operational day and
// reports that landing slot in `resulting_slot`. Callers surface THAT slot in
// the defer-confirmation toast instead of their locally-known rolled-to values.
export const respondPickupSlot = async (
  uid: string,
  choice: 'accept' | 'change' | 'defer',
  timeSlotCode?: string,
  preferredTiming?: PreferredTiming | null,
): Promise<SlotResponseResult> => {
  const response = await axiosJSONInstance.post(`/merchants/pick-ups/${uid}/slot-response`, {
    choice,
    ...(timeSlotCode ? { time_slot_code: timeSlotCode } : {}),
    ...(preferredTiming !== undefined ? { preferred_timing: preferredTiming } : {}),
  });
  return response.data?.object ?? response.data;
};
